import {
  addMonths,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
} from 'date-fns';

export type Seconds = number;
export type Milliseconds = number;
const DAY_IN_MILLISECONDS = 86400000;

// TODO use toUtcTimeZone only on api requests and to convert it with fromUtcToLocalTimeZone into app
export const toUtcTimeZone = function (date: Milliseconds): Milliseconds {
  return date - new Date(date).getTimezoneOffset() * 60000;
};
export const fromUtcToLocalTimeZone = function (date: Milliseconds): Milliseconds {
  return date + new Date(date).getTimezoneOffset() * 60000;
};

export const secondsToMilliseconds = function (date: Seconds): Milliseconds {
  return date * 1000;
};

export const millisecondsToSeconds = function (date: Milliseconds): Seconds {
  return Math.floor(date / 1000);
};

export const startOfQuarterMilliseconds = function (date: Milliseconds): Milliseconds {
  return startOfQuarter(date).getTime();
};

export const endOfQuarterMilliseconds = function (date: Milliseconds): Milliseconds {
  return endOfQuarter(date).getTime();
};

export const oneMonthBeforeMilliseconds = function (date: Milliseconds): Milliseconds {
  return subMonths(date, 1).getTime();
};

export const addMonth = function (date: Milliseconds): Milliseconds {
  return addMonths(date, 1).getTime();
};

export const addDays = function (date: Milliseconds, numberOfDays: number): Milliseconds {
  return date + numberOfDays * DAY_IN_MILLISECONDS;
};

export const subDays = function (date: Milliseconds, numberOfDays: number): Milliseconds {
  return date - numberOfDays * DAY_IN_MILLISECONDS;
};

export const startOfDayUtc = function (date: Milliseconds): Milliseconds {
  return startOfDay(date).getTime();
};

export const endOfDayUtc = function (date: Milliseconds): Milliseconds {
  return endOfDay(date).getTime();
};

export const startOfYearMilliseconds = function (date: Milliseconds): Milliseconds {
  return startOfYear(date).getTime();
};

export const endOfYearMilliseconds = function (date: Milliseconds): Milliseconds {
  return endOfYear(date).getTime();
};

export const endOfMonthMilliseconds = function (date: Milliseconds): Milliseconds {
  return endOfMonth(date).getTime();
};

export const startOfMonthMilliseconds = function (date: Milliseconds): Milliseconds {
  return startOfMonth(date).getTime();
};

export const getMonthName = (month: number): string => {
  switch (month) {
    case 0:
      return 'consumption_detail.month_january_l';
    case 1:
      return 'consumption_detail.month_february_l';
    case 2:
      return 'consumption_detail.month_march_l';
    case 3:
      return 'consumption_detail.month_april_l';
    case 4:
      return 'consumption_detail.month_may_l';
    case 5:
      return 'consumption_detail.month_june_l';
    case 6:
      return 'consumption_detail.month_july_l';
    case 7:
      return 'consumption_detail.month_august_l';
    case 8:
      return 'consumption_detail.month_september_l';
    case 9:
      return 'consumption_detail.month_october_l';
    case 10:
      return 'consumption_detail.month_november_l';
    case 11:
      return 'consumption_detail.month_december_l';
    default:
      return '';
  }
};

export const dateStringToMilliseconds = (date: string): Milliseconds => {
  return new Date(date).getTime();
};

/**
 * Converts a content date to milliseconds.
 *
 * The content V2 endpoints return dates as ISO strings (`2025-08-29T00:00:00`), while the V1 public
 * content endpoints still return epoch seconds (`1665619200`). Both have to be understood as long
 * as the public queries are on V1.
 *
 * @returns the date in milliseconds, or null when the value is missing or unparseable
 */
export const contentDateToMilliseconds = (
  date: string | number | null | undefined
): Milliseconds | null => {
  if (date === null || date === undefined || date === '') {
    return null;
  }

  // Epoch seconds arrive as a plain number or a string of digits only.
  const isEpochSeconds = typeof date === 'number' || /^\d+$/.test(date);
  const milliseconds = isEpochSeconds
    ? secondsToMilliseconds(Number(date))
    : new Date(date).getTime();

  return Number.isNaN(milliseconds) ? null : milliseconds;
};

export const toLocalDayKey = (date: Milliseconds | Date): string => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Normalizes a date to a timezone-safe ISO string for date-only values.
 * This prevents timezone shifts that could change the selected date when converting to UTC.
 *
 * @param dateValue - Date object, date string, or any date-like value
 * @returns ISO string in format YYYY-MM-DDTHH:mm:ss.sssZ representing the selected date at midnight UTC
 *
 * @example
 * // User selects Aug 17, 2000 in any timezone
 * normalizeDateToISOString(new Date(2000, 7, 17)) // Returns "2000-08-17T00:00:00.000Z"
 */
export const normalizeDateToISOString = (dateValue: Date | string | number): string => {
  const date = new Date(dateValue);

  // Use local date components to prevent timezone shifts
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}T00:00:00.000Z`;
};
